import type { PropsWithChildren, SyntheticEvent } from "react";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import TextField from "@mui/material/TextField";
import { DateTimeFormatRepository } from "consts/general";
import { getFormInputProps } from "util/forms";
import { adultDate } from "util/support";
import { ProofFileInput, acceptRules, IdentityPlaceholderTitle } from "ui/forms/formik-elements/FileInput";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import OnboardingForm from "ui/forms/ECommerce/Onboarding/OnboardingForm";
import { OnboardingInputControl } from "ui/molecules/Input";
import { FormField, type OnboardingIdentityVerificationProps } from "./types";
import Autocomplete from "ui/atoms/Autocomplete";
import { Option } from "types";
import { getOptionById } from "util/option";

const UBOIdentityVerification = ({
    values,
    isSubmitting,
    isValid,
    initialValues,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    isBootstrapDataLoading,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    children,
    ...form
}: PropsWithChildren<OnboardingIdentityVerificationProps>) => {


    const onAutocompleteChange = (
        formField: string,
        valueGetter?: (newValue: any) => any
    ) => (_: SyntheticEvent | undefined, newValue: any) => {
        const valueToSet = Array.isArray(newValue)
            ? newValue
            : valueGetter
                ? valueGetter(newValue)
                : newValue;

        setFieldValue(formField, valueToSet, true);
        setTimeout(() => setFieldTouched(formField, true));
    };

    const getOptionLabel = ({ name }: Option) => name;
    const getOptionId = ({ id }: Option) => id;

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <OnboardingInputControl
                name={`${values.pki}.${FormField.FullName}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.FullName, form)}
                        autoFocus
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.FullName}
                        value={values[FormField.FullName]}
                        label='Full Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={`${values.pki}.${FormField.DateOfBirth}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            disabled={disabled}
                            maxDate={adultDate()}
                            format={DateTimeFormatRepository.Default}
                            label='Date of Birth'
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(FormField.DateOfBirth, form),
                                    name: FormField.DateOfBirth,
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur
                                }
                            }}
                            value={values[FormField.DateOfBirth]}
                            onChange={value => {
                                setFieldValue(FormField.DateOfBirth, value);
                                setTimeout(() => setFieldTouched(FormField.DateOfBirth, true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <OnboardingInputControl
                name={`${values.pki}.${FormField.Nationality}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.Nationality] ?? ''}
                        onChange={onAutocompleteChange(FormField.Nationality)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Nationality, form),
                            required: true,
                            name: FormField.Nationality,
                            label: 'Nationality'
                        }}
                    />
                )}
            />

            <OnboardingInputControl
                name={`${values.pki}.${FormField.ResidentialAddress}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.ResidentialAddress, form)}
                        required
                        multiline
                        fullWidth
                        disabled={disabled}
                        rows={4}
                        size='small'
                        name={FormField.ResidentialAddress}
                        value={values[FormField.ResidentialAddress]}
                        label='Residential Address'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />

            <OnboardingInputControl
                name={FormField.LegalForm}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <Autocomplete
                        fullWidth
                        disablePortal
                        disabled={disabled}
                        size='small'
                        layout={null}
                        loading={isBootstrapDataLoading}
                        options={bootstrapData.legalForm}
                        value={getOptionById(bootstrapData.legalForm, values[FormField.LegalForm]) || null}
                        getOptionLabel={getOptionLabel}
                        onChange={onAutocompleteChange(FormField.LegalForm, getOptionId)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.LegalForm, form),
                            required: true,
                            label: 'Legal Form',
                            name: FormField.LegalForm
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Proof of Identity*
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Passport or Government-Issued ID
            </Typography>
            <OnboardingInputControl
                name={`${values.pki}.${FormField.IdentityFileKey}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.IdentityFileKey}
                        file={values[FormField.IdentityFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Proof of Address*
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Recent utility bill or official correspondence (dated within the last 3 months).
            </Typography>
            <OnboardingInputControl
                name={`${values.pki}.${FormField.AddressFileKey}`}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.AddressFileKey}
                        file={values[FormField.AddressFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                    />
                )}
            />
            {children}
        </OnboardingForm>
    );
};

export default UBOIdentityVerification;
